import type { AttendanceEmployee } from '../mappers/timelogMapper';
import type { LocalEmployeeRepository } from '@/modules/employees/infrastructure/repositories/LocalEmployeeRepository';
import type { RemoteEmployeeLookupRepository } from './RemoteEmployeeLookupRepository';
import { enrichEmployeePhotoUrl } from '../helpers/enrichEmployeePhotoUrl';

/**
 * IEmployeeLookupRepository — port for employee lookup during timelog flows.
 * Both local and remote implementations satisfy this shape.
 */
export interface IEmployeeLookupRepository {
  findByCodeOrDui(accessRoleId: number, value: string): Promise<AttendanceEmployee | null>;
  findByCardNumber(accessRoleId: number, cardNumber: string): Promise<AttendanceEmployee | null>;
}

/**
 * CompositeEmployeeLookupRepository — local-first lookup with remote fallback.
 *
 * Strategy:
 *   1. Query local.findByCodeOrDui → if local store has rows for this role,
 *      use only the local result (even if null — genuine miss in populated store).
 *   2. If local store is EMPTY for this role (count === 0), fall back to remote.
 *      This handles first launch when IndexedDB hasn't been seeded yet.
 *
 * After the first successful sync (triggered by HomePage on mount),
 * remote is never called again during identification.
 */
export class CompositeEmployeeLookupRepository implements IEmployeeLookupRepository {
  constructor(
    private readonly local:  Pick<LocalEmployeeRepository, 'findByCodeOrDui' | 'findByCardNumber' | 'countByAccessRole'>,
    private readonly remote: Pick<RemoteEmployeeLookupRepository, 'findByCodeOrDui' | 'findByCardNumber'>,
  ) {}

  async findByCodeOrDui(accessRoleId: number, value: string): Promise<AttendanceEmployee | null> {
    // Run count + lookup concurrently — single round-trip to IndexedDB
    const [count, localResult] = await Promise.all([
      this.local.countByAccessRole(accessRoleId),
      this.local.findByCodeOrDui(accessRoleId, value),
    ]);

    let result: AttendanceEmployee | null;
    if (count > 0) {
      // Store has rows — local result is authoritative (even if null = genuine miss)
      result = localResult;
    } else {
      // Store is empty → first-launch fallback: ask remote
      result = await this.remote.findByCodeOrDui(accessRoleId, value);
    }

    // Phase 6: enrich with photoUrl from cached EmployeeDTO
    return result ? enrichEmployeePhotoUrl(result) : null;
  }

  /**
   * Card lookup — always local-first.
   * Remote has no card-lookup endpoint; remote stub returns null as fallback.
   */
  async findByCardNumber(accessRoleId: number, cardNumber: string): Promise<AttendanceEmployee | null> {
    const localResult = await this.local.findByCardNumber(accessRoleId, cardNumber);
    if (localResult !== null) {
      // Phase 6: enrich with photoUrl from cached EmployeeDTO
      return enrichEmployeePhotoUrl(localResult);
    }
    // Remote stub — always null (no card endpoint in API v1)
    const remoteResult = await this.remote.findByCardNumber(accessRoleId, cardNumber);
    return remoteResult ? enrichEmployeePhotoUrl(remoteResult) : null;
  }
}
